<template>
  <div class="tz-qna" :class="{ 'tz-qna--accordion': isAccordion }">
    <header v-for="item in renderIf(title)" :key="item" class="tz-qna__title">
      <img
        v-for="item in renderIf(titleIcon)"
        :key="item"
        class="tz-qna__title-icon"
        loading="lazy"
        alt=""
        aria-hidden="true"
        :src="titleIcon"
      />
      <h2 class="tz-qna__title-label" v-text="title" />
      <div
        v-for="item in renderIf(subtext)"
        :key="item"
        class="tz-qna__subtext"
      >
        <tz-markdown :content="subtext" />
      </div>
    </header>
    <dl
      v-for="item in renderIf(qnaItems)"
      :key="item"
      class="tz-qna__list"
      :itemscope="hasItemscope"
      :itemtype="getQnaListItemType"
    >
      <div
        class="tz-qna__item"
        :class="{ 'tz-qna__item--expanded': isAriaExpanded(index) }"
        v-for="({ question, answer }, index) in qnaItems.filter(
          ({ question, answer }) => question && answer
        )"
        :key="index"
        :itemscope="hasItemscope"
        :itemtype="getQnaItemType"
        :itemprop="getQnaItemProp"
      >
        <dt
          class="tz-qna__question"
          v-on:click="handleClick(index)"
          :aria-expanded="getAriaExpanded(index)"
        >
          <img
            v-for="item in renderIf(questionIcon)"
            :key="item"
            class="tz-qna__question-icon"
            loading="lazy"
            alt=""
            aria-hidden="true"
            :src="questionIcon"
          />
          <span
            class="tz-qna__question-label"
            :itemprop="getQnaQuestionItemProp"
            v-text="question"
          />
        </dt>
        <dd
          v-for="answerText in renderIf(hasMarkdown)"
          :key="answerText"
          class="tz-qna__answer"
          :hidden="isHidden(index)"
          :itemscope="hasItemscope"
          :itemprop="getQnaAnswerItemProp"
          :itemtype="getQnaAnswerItemType"
        >
          <tz-markdown
            :content="getAnswer(answer)"
            :itemprop="getQnaAnswerItemText"
          />
        </dd>
        <dd
          v-for="answerText in renderIf(!hasMarkdown)"
          :key="answerText"
          class="tz-qna__answer"
          :hidden="isHidden(index)"
          :itemscope="hasItemscope"
          :itemprop="getQnaAnswerItemProp"
          :itemtype="getQnaAnswerItemType"
        >
          <span
            class="tz-qna__answer-text"
            :itemprop="getQnaAnswerItemText"
            v-text="answer"
          />
        </dd>
      </div>
    </dl>
  </div>
</template>

<script>
import TzMarkdown from '@trazi/tz-markdown/src/tz-markdown.vue';
import marked from 'marked';
const answerPattern = new RegExp('\\*', 'g');
const NBars = {
  regex: new RegExp('{{([^}]+)}}', 'g'),
  transform: function(template, objs) {
    return template.replace(this.regex, (match, prop) => {
      return objs[prop];
    });
  },
  compile: function(template) {
    return objs => {
      return this.transform(template, objs);
    };
  }
};

export default {
  name: 'tz-qna',
  components: {
    TzMarkdown
  },
  data() {
    return {
      currentExpandedIndex: 0,
      faqLd: JSON,
      qnaItems: []
    };
  },
  computed: {
    getQnaAnswerItemProp() {
      return this.returnIfItemType('acceptedAnswer');
    },
    getQnaAnswerItemText() {
      return this.returnIfItemType('text');
    },
    getQnaAnswerItemType() {
      return this.returnIfItemType('https://schema.org/Answer');
    },
    getQnaItemProp() {
      return this.returnIfItemType('mainEntity');
    },
    getQnaItemType() {
      return this.returnIfItemType('https://schema.org/Question');
    },
    getQnaListItemType() {
      return this.returnIfItemType('https://schema.org/FAQPage');
    },
    getQnaQuestionItemProp() {
      return this.returnIfItemType('name');
    }
  },
  created() {
    this.currentExpandedIndex = this.expandedIndex;
    this.setQnaItems();
    this.setFaqLd();
  },
  methods: {
    handleClick(index) {
      if (!this.isAccordion) {
        return;
      } else if (this.toggleItems) {
        this.qnaItems[index].expanded = !this.qnaItems[index].expanded;
        return;
      } else if (index === this.currentExpandedIndex) {
        this.currentExpandedIndex = -1;
        return;
      }

      this.currentExpandedIndex = index;
    },
    isHidden(index) {
      if (!this.isAccordion) {
        return false;
      } else if (this.toggleItems) {
        return !this.qnaItems[index].expanded;
      }

      return index !== this.currentExpandedIndex;
    },
    isAriaExpanded(index) {
      if (!this.isAccordion) {
        return false;
      } else if (this.toggleItems) {
        return this.qnaItems[index].expanded;
      }

      return index === this.currentExpandedIndex;
    },
    getAriaExpanded(index) {
      if (!this.isAccordion) {
        return false;
      }

      return this.isAriaExpanded(index) ? 'true' : 'false';
    },
    /**
     * If statement vue hack to prevent <!-- --> comment
     * @param condition
     * @returns {[]}
     */
    renderIf(condition) {
      return condition ? [Math.random()] : [];
    },
    /**
     * Set's the Json Schema
     */
    setFaqLd() {
      if (!this.hasSchema || this.hasItemscope) {
        return;
      }

      /**
       * Escape html
       * @param text
       * @returns {*}
       */
      const htmlEscapeToText = text => {
        return text.replace(/&#[0-9]*;|&amp;/g, function(escapeCode) {
          if (escapeCode.match(/amp/)) {
            return '&';
          }

          return String.fromCharCode(escapeCode.match(/[0-9]+/));
        });
      };

      /**
       * Render plain method
       * @returns {marked.Renderer}
       */
      const renderPlain = () => {
        const render = new marked.Renderer();

        // render just the text of a link
        render.link = function(href, title, text) {
          return text;
        };

        // render just the text of a paragraph
        render.paragraph = function(text) {
          return htmlEscapeToText(text) + '\r\n';
        };

        // render just the text of a heading element, but indicate level
        render.heading = function(text, level) {
          return level + ' ) ' + text;
        };

        // render just the text of the strong tag
        render.strong = function(text) {
          return htmlEscapeToText(text) + '\r\n';
        };

        // render nothing for images
        render.image = function() {
          return '';
        };

        return render;
      };

      this.faqLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: []
      };
      this.qnaItems.forEach(({ question, answer }) => {
        this.faqLd.mainEntity.push({
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: marked(answer, { renderer: renderPlain() })
          }
        });
      }, this);
    },
    /**
     * Shuffle's array values in a random order
     * @param arr
     * @returns {array}
     */
    shuffle(arr) {
      return arr
        .map(a => [Math.random(), a])
        .sort((a, b) => a[0] - b[0])
        .map(a => a[1]);
    },
    /**
     * Picks random template for questions and answers
     * @param questionItems
     * @param answerItems
     * @param {Boolean} expanded
     * @returns {{question: {string}, answer: {string}, expanded: {Boolean}}}
     */
    getQnaItemFromVariations(questionItems, answerItems, expanded) {
      const { question } = this.shuffle(questionItems)[0];
      const { multiple_values, no_value, singular_value } = this.shuffle(
        answerItems
      )[0];

      let answer = '';
      if (multiple_values && answerItems.length) {
        answer = multiple_values;
      } else if (singular_value && answerItems.length) {
        answer = singular_value;
      } else {
        answer = no_value;
      }

      return this.getQnaItem(question, answer, expanded);
    },
    /**
     * Get the question and answer and process template if there's tokens
     * @param {string} question
     * @param {string} answer
     * @param {Boolean} expanded
     * @returns {{question, answer, expanded}}
     */
    getQnaItem(question, answer, expanded) {
      if (this.tokens) {
        return {
          question: NBars.transform(question, this.tokens),
          answer: NBars.transform(answer, this.tokens),
          expanded
        };
      }
      return {
        question,
        answer,
        expanded
      };
    },
    /** Return the text if hasItemscope is true **/
    returnIfItemType(text) {
      return this.hasItemscope ? text : false;
    },
    /**
     * Set's the qnaItems value
     */
    setQnaItems() {
      let qnaItems = [];
      // Loop through array and figure out if each object has variations or not
      for (let i = 0; i < this.items.length; i++) {
        const { questions, answers, question, answer, expanded } = this.items[
          i
        ];
        if (questions && answers) {
          qnaItems.push(
            this.getQnaItemFromVariations(questions, answers, !!expanded)
          );
        } else if (question && answer) {
          qnaItems.push(this.getQnaItem(question, answer, !!expanded));
        }
      }
      this.qnaItems = this.hasShuffle ? this.shuffle(qnaItems) : qnaItems;
      if (this.expandedIndex >= 0) {
        this.qnaItems[this.expandedIndex].expanded = true;
      }
    },
    /**
     * Escape '*' to fix issue with markdown
     */
    getAnswer(answer) {
      if (typeof answer !== 'string') {
        return '';
      }

      return answer.replace(answerPattern, '\\*');
    }
  },
  props: {
    // Title if you'd like to have one
    title: {
      type: String
    },
    // Subtext if you'd like to have one
    subtext: {
      type: String
    },
    // Icon that would go with the title
    titleIcon: {
      type: String
    },
    // Icon that would go with each question
    questionIcon: {
      type: String
    },
    // An array of questions and answers. Expects format of [{question: string, answer: string}]
    items: {
      type: Array,
      required: true
    },
    // If true, use itemscope rather than ld+json
    hasItemscope: {
      type: Boolean,
      default: false
    },
    // Do the answers use markdown?
    hasMarkdown: {
      type: Boolean,
      required: false,
      default: true
    },
    // Shuffle items?
    hasShuffle: {
      type: Boolean,
      default: false
    },
    // If ld json schema should show for seo
    hasSchema: {
      type: Boolean,
      required: false,
      default: true
    },
    // Is the FAQ an accordion?
    isAccordion: {
      type: Boolean,
      required: false,
      default: false
    },
    // Toggle each accordion item on and off
    toggleItems: {
      type: Boolean,
      required: false,
      default: false
    },
    // If FAQ is an accordion what's the default index to expand (set to -1 if you don't want a default index opened)
    expandedIndex: {
      type: Number,
      require: false,
      default: 0
    },
    // Tokens are used with templates
    tokens: {
      type: Object,
      default: null
    }
  },
  head() {
    if (!this.hasSchema || this.hasItemscope) {
      return;
    }

    return {
      script: [{ type: 'application/ld+json', json: this.faqLd }]
    };
  }
};
</script>
